/**
 * Rifinitura del menù per una singola organizzazione, serializzata in `Licenza.menuConfig`.
 *
 * È sempre e solo SOTTRATTIVA: può nascondere voci già coperte da licenza e ruolo, mai renderne
 * visibile una che il legame voce → modulo/ruoli non copre.
 */
export interface MenuConfig {
  /** Interruttore della gestione per questa organizzazione. False (default) = menù come prima. */
  enabled: boolean;
  /** Id di voce spenti per tutti i ruoli dell'organizzazione. */
  disabled: string[];
  /** Id di voce spenti per singolo ruolo, chiave = codice ruolo (es. 'MANAGER'). */
  ruoli: Record<string, string[]>;
}

export function emptyMenuConfig(): MenuConfig {
  return {enabled: false, disabled: [], ruoli: {}};
}

/**
 * Parsing tollerante: qualunque contenuto inatteso (null, JSON malformato, tipi sbagliati) degrada
 * a configurazione vuota, cioè al comportamento precedente. Non solleva mai eccezioni.
 */
export function parseMenuConfig(json?: string | null): MenuConfig {
  if (!json) return emptyMenuConfig();

  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    console.warn('menuConfig non è un JSON valido: si applica il comportamento predefinito.');
    return emptyMenuConfig();
  }

  if (!raw || typeof raw !== 'object') return emptyMenuConfig();

  const source = raw as Partial<Record<keyof MenuConfig, unknown>>;
  const config = emptyMenuConfig();
  config.enabled = source.enabled === true;
  config.disabled = toIdArray(source.disabled);

  if (source.ruoli && typeof source.ruoli === 'object') {
    for (const [ruolo, voci] of Object.entries(source.ruoli as Record<string, unknown>)) {
      config.ruoli[ruolo] = toIdArray(voci);
    }
  }

  return config;
}

export function serializeMenuConfig(config: MenuConfig): string {
  return JSON.stringify(config);
}

function toIdArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string' && v.length > 0)
    : [];
}
